// BrandMark — the ehjizli mark (two arcs) as INLINE SVG so its approved
// motions can run (board 21 Logo v2 «د · الحركة»; the ERP handoff replaces the
// dot vocabulary with these). Geometry is the 200-unit grid of
// public/brand/logo-ehjizli*.svg: upper arc r72 centred (105,31), lower arc r97
// centred (105,230), stroke 30. Motion lives in brand-mark.css.
//
//   static  — the mark, no motion.
//   launch  — 1.05 s once: upper arc draws left→right (600 ms), lower arc
//             right→left 150 ms later, then the two meet with a 1.06 pulse.
//   loading — 2 s loop: the whole mark rocks ±8° about (105,230) («الكرسي الهزّاز»).
//   success — once and holds: the upper arc lifts away (300 ms), a check is drawn
//             into the lower arc (450 ms), pop 1.05. Stays until the screen closes.
//   failed  — 0.8 s once: the arcs part ∓10 units, shake ±4 twice, stay apart.
//   empty   — static: the arcs' fill outline dashed in mist over a white fill.
//   pulse   — 1.2 s loop: fades to 45 % and back (busy buttons, light refresh).
//
// prefers-reduced-motion: every motion becomes a 400 ms fade of its end state;
// loading becomes a dim to 55 % (board «تقليل الحركة»).

import { useEffect, useState } from 'react';
import './brand-mark.css';

export type BrandMarkMode = 'static' | 'launch' | 'loading' | 'success' | 'failed' | 'empty' | 'pulse';
export type BrandMarkTone = 'brand' | 'white' | 'ink' | 'mist';

export interface BrandMarkProps {
  mode?: BrandMarkMode;
  /** Rendered width/height in px (smallest legible size is 16 — board rule). */
  size?: number;
  tone?: BrandMarkTone;
  /** Accessible name; omitted ⇒ decorative (aria-hidden). */
  label?: string;
  className?: string;
  'data-testid'?: string;
}

/** Upper arc (the «seat»), drawn from its left end (33,31). Length ≈ 180. */
export const MARK_TOP = 'M33 31A72 72 0 0 0 162.5 74.33';
/** Lower arc (the «base»), drawn from its right end (171.15,159.06). Length ≈ 146. */
export const MARK_BOTTOM = 'M171.15 159.06A97 97 0 0 0 38.85 159.06';
/** Success check A(58,66) → V(90,104) → B(160,33). Length ≈ 150. */
export const MARK_CHECK = 'M58 66L90 104L160 33';
/** Fill outlines of the two arcs (the «empty» state). */
export const MARK_TOP_OUTLINE = 'M18 31A87 87 0 0 0 174.48 83.36L150.52 65.3A57 57 0 0 1 48 31Z';
export const MARK_BOTTOM_OUTLINE = 'M28.62 148.09A112 112 0 0 1 181.38 148.09L160.92 170.03A82 82 0 0 0 49.08 170.03Z';

const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';

function reducedNow(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_QUERY).matches;
  } catch {
    return false;
  }
}

/** Tracks the OS «reduce motion» setting. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(reducedNow);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(REDUCED_QUERY);
    } catch {
      return;
    }
    const on = () => setReduced(mql.matches);
    on();
    mql.addEventListener?.('change', on);
    return () => mql.removeEventListener?.('change', on);
  }, []);
  return reduced;
}

export function BrandMark({
  mode = 'static',
  size = 48,
  tone,
  label,
  className,
  'data-testid': testId,
}: BrandMarkProps) {
  const reduced = useReducedMotion();
  const t: BrandMarkTone = tone ?? (mode === 'empty' ? 'mist' : 'brand');
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true as const };
  const cls = ['ehz', `ehz--${mode}`, className].filter(Boolean).join(' ');

  if (mode === 'empty') {
    return (
      <svg
        className={cls}
        viewBox="0 0 200 200"
        width={size}
        height={size}
        data-mode={mode}
        data-tone={t}
        data-reduced={reduced ? 'true' : undefined}
        data-testid={testId}
        focusable="false"
        {...a11y}
      >
        <path className="ehz-outline" d={MARK_TOP_OUTLINE} />
        <path className="ehz-outline" d={MARK_BOTTOM_OUTLINE} />
      </svg>
    );
  }

  return (
    <svg
      className={cls}
      viewBox="0 0 200 200"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={30}
      data-mode={mode}
      data-tone={t}
      data-reduced={reduced ? 'true' : undefined}
      data-testid={testId}
      focusable="false"
      {...a11y}
    >
      <g className="ehz-g">
        <path className="ehz-top" d={MARK_TOP} />
        {mode === 'success' && <path className="ehz-check" d={MARK_CHECK} strokeLinejoin="round" />}
        <path className="ehz-bottom" d={MARK_BOTTOM} />
      </g>
    </svg>
  );
}
